import React from 'react';
import styled from 'styled-components';
import { Users, Ban, Eye, Edit3 } from 'lucide-react';
import { Drawer, Badge, Button, formatPhone, formatCpf } from '@kardume/ui';
import type { BackofficeEmployeeDto, AccountStatus, Tone } from '@kardume/types';
import { BACKOFFICE_PERMISSION_LABELS } from '@kardume/types';

const NameLine = styled.p`
  margin: 0 0 1rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.textPrimary};
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Stat = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.85rem 1rem;

  .label {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .value {
    margin-top: 0.25rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.1rem;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.85rem;
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  svg {
    color: ${(p) => p.theme.primary};
  }
`;

const Field = styled.div`
  margin-bottom: 0.7rem;

  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const PermList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Muted = styled.p`
  color: ${(p) => p.theme.textMuted};
  font-size: 0.88rem;
  margin: 0;
`;

const ROLE_LABELS: Record<BackofficeEmployeeDto['role'], string> = {
  BACKOFFICE: 'Backoffice',
  ADMIN: 'Administrador',
};

const STATUS_LABELS: Record<AccountStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING_ACTIVATION: 'Pendente',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

function statusTone(status: AccountStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const isAccessAllowed = (e: BackofficeEmployeeDto): boolean => e.accessEnabled ?? e.status === 'ACTIVE';

interface EmployeeDetailDrawerProps {
  employee: BackofficeEmployeeDto | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (employee: BackofficeEmployeeDto) => void;
}

const EmployeeDetailDrawer: React.FC<EmployeeDetailDrawerProps> = ({ employee, isOpen, onClose, onEdit }) => {
  const fullName = employee ? `${employee.firstName} ${employee.lastName}` : '';
  const allowed = employee ? isAccessAllowed(employee) : false;
  const profileLabel = employee?.role === 'ADMIN' ? 'Acesso total' : employee?.accessProfileName ?? 'Sem perfil';

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Funcionário"
      headerActions={
        employee && (
          <>
            <Badge tone={statusTone(employee.status)}>{STATUS_LABELS[employee.status]}</Badge>
            <Badge tone={allowed ? 'success' : 'danger'}>{allowed ? 'Acesso liberado' : 'Acesso bloqueado'}</Badge>
            <Button variant="outline" onClick={() => onEdit(employee)}>
              <Edit3 size={15} /> Editar
            </Button>
          </>
        )
      }
    >
      {employee && (
        <>
          <NameLine>{fullName}</NameLine>

          <Stats>
            <Stat>
              <div className="label">Cargo</div>
              <div className="value">{ROLE_LABELS[employee.role]}</div>
            </Stat>
            <Stat>
              <div className="label">Status</div>
              <div className="value">{STATUS_LABELS[employee.status]}</div>
            </Stat>
            <Stat>
              <div className="label">Perfil de acesso</div>
              <div className="value">{profileLabel}</div>
            </Stat>
            <Stat>
              <div className="label">Permissões efetivas</div>
              <div className="value">{employee.permissions.length}</div>
            </Stat>
          </Stats>

          <Grid>
            <Section>
              <h4>
                <Users size={16} /> Dados cadastrais
              </h4>
              <Field>
                <div className="k">Nome</div>
                <div className="v">{fullName}</div>
              </Field>
              <Field>
                <div className="k">E-mail</div>
                <div className="v">{employee.email}</div>
              </Field>
              <Field>
                <div className="k">Telefone</div>
                <div className="v">{employee.phone ? formatPhone(employee.phone) : '-'}</div>
              </Field>
              <Field>
                <div className="k">CPF</div>
                <div className="v">{employee.document ? formatCpf(employee.document) : '-'}</div>
              </Field>
              <Field>
                <div className="k">ID</div>
                <div className="v" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {employee.id}
                </div>
              </Field>
              <Field>
                <div className="k">Acesso</div>
                <div className="v">{allowed ? 'Liberado' : 'Bloqueado'}</div>
              </Field>
            </Section>

            <Section>
              <h4>
                <Eye size={16} /> Permissões efetivas
              </h4>
              {employee.permissions.length === 0 ? (
                <Muted>
                  <Ban size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Nenhuma permissão atribuída.
                </Muted>
              ) : (
                <PermList>
                  {employee.permissions.map((code) => (
                    <Badge key={code} tone="secondary">
                      {BACKOFFICE_PERMISSION_LABELS[code] ?? code}
                    </Badge>
                  ))}
                </PermList>
              )}
            </Section>
          </Grid>
        </>
      )}
    </Drawer>
  );
};

export default EmployeeDetailDrawer;
